/**
 * SKU rate-of-sale ingestion boundary + UI options.
 *
 * This module is the SINGLE place the app projects a SKU's real per-window
 * rate-of-sale fields into resolver anchors, and the SINGLE place it declares
 * which windows currently have real data. It mirrors the C# ingestion boundary
 * `Domain.RateOfSale.SkuRateOfSaleExtensions.BuildRateOfSaleAnchors`.
 *
 * When the real per-window ROS data lands as new `Sku` fields, extend
 * {@link buildRateOfSaleAnchors} and {@link REAL_RATE_OF_SALE_WINDOWS} here — and
 * ONLY here. Every consumer resolves through `resolveRateOfSale`, so a newly
 * supplied window is automatically preferred as a real anchor and its
 * "estimated" label disappears with no other code change.
 */

import type { Sku } from "../../redistribution/models/Sku.js";
import {
  SUPPORTED_RATE_OF_SALE_WINDOWS,
  type RateOfSaleAnchors,
} from "./RateOfSaleResolver.ts";

/**
 * The interim set of windows (days) that have REAL per-window rate-of-sale data.
 *
 * Today only the three windows the API/stored procedure supplies are real
 * (`roS30`/`roS60`/`roS90`). Every other supported window is estimated by the
 * resolver until the real-data source provides it. This list grows — alongside
 * {@link buildRateOfSaleAnchors} — as the other developer's real per-window data
 * lands, at which point the affected windows stop rendering "estimated".
 */
export const REAL_RATE_OF_SALE_WINDOWS: readonly number[] = [
  30, 60, 90,
] as const;

/**
 * Projects a SKU's real per-window rate-of-sale fields into the
 * `window (days) -> ros` anchor map consumed by `resolveRateOfSale`.
 *
 * This is the ingestion boundary (mirror of the C# `BuildRateOfSaleAnchors`).
 * Windows whose value is null/undefined are omitted so the resolver estimates
 * them rather than treating a missing window as a real zero.
 */
export function buildRateOfSaleAnchors(sku: Sku): RateOfSaleAnchors {
  const anchors: Record<number, number> = {};

  // Add new real windows here (and ONLY here) as the real-data source lands.
  if (sku.roS30 != undefined) {
    anchors[30] = sku.roS30;
  }
  if (sku.roS60 != undefined) {
    anchors[60] = sku.roS60;
  }
  if (sku.roS90 != undefined) {
    anchors[90] = sku.roS90;
  }

  return anchors;
}

/** A selectable rate-of-sale window for the slider/dropdown. */
interface RateOfSaleOption {
  value: number;
  label: string;
  /** True when this window has no real data yet and is resolved as an estimate. */
  estimated: boolean;
}

/**
 * Human-readable label for a rate-of-sale window (days). Maps the canonical
 * window numbers to friendly units ("1 week", "3 months", "1 year"), with a
 * generic fallback so any future window still renders sensibly.
 */
function rateOfSaleWindowLabel(window: number): string {
  const named: Record<number, string> = {
    7: "1 week",
    14: "2 weeks",
    21: "3 weeks",
    28: "4 weeks",
    30: "1 month",
    365: "1 year",
  };

  const namedLabel = named[window];
  if (namedLabel != undefined) {
    return namedLabel;
  }

  if (window < 7) {
    return `${String(window)} ${window === 1 ? "day" : "days"}`;
  }

  if (window % 30 === 0) {
    return `${String(window / 30)} months`;
  }

  return `${String(window)} days`;
}

/**
 * Builds the rate-of-sale options list from the canonical
 * {@link SUPPORTED_RATE_OF_SALE_WINDOWS} (the single source of truth kept in sync
 * with the API validator). Each option is flagged `estimated` when its window is
 * not in {@link REAL_RATE_OF_SALE_WINDOWS}.
 */
export function buildRateOfSaleOptions(): RateOfSaleOption[] {
  return SUPPORTED_RATE_OF_SALE_WINDOWS.map((window) => ({
    value: window,
    label: rateOfSaleWindowLabel(window),
    estimated: !REAL_RATE_OF_SALE_WINDOWS.includes(window),
  }));
}
